import { pictureManager } from "@/services/utilities/picture-manager";

// Couleurs de repli front, centralisées ici (revue Thibault) plutôt que recopiées dans chaque
// composant - ICON_FALLBACK_COLOR est un gris neutre propre au front (aucune variable SCSS ne
// porte cette valeur) ; DOMAIN_COLOR_FALLBACK mirrore $landing-navy (_variables.scss), JS ne
// pouvant pas importer une variable SCSS directement - les deux valeurs doivent rester alignées à
// la main si l'une des deux change.
export const ICON_FALLBACK_COLOR = "#9aa0a6";
export const DOMAIN_COLOR_FALLBACK = "#1d2b3a";

class ColorManager {
  HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

  isHex(value) {
    return typeof value === "string" && this.HEX_PATTERN.test(value.trim());
  }

  normalizeHex(value) {
    if (!this.isHex(value)) {
      return null;
    }
    const hex = value.trim().toLowerCase();
    // Notation courte (#fff) étendue en notation longue pour permettre l'ajout d'un canal alpha
    return hex.length === 4 ? "#" + hex.slice(1).replace(/./g, (char) => char + char) : hex;
  }

  // Le back ne valide pas le format des couleurs stockées : certaines n'ont pas de "#" (ex. "ff0000")
  toHex(value) {
    if (typeof value !== "string") {
      return null;
    }
    const trimmed = value.trim();
    return this.normalizeHex(trimmed.startsWith("#") ? trimmed : "#" + trimmed);
  }

  hexWithAlpha(value, alpha = 0.2) {
    const hex = this.normalizeHex(value);
    if (!hex) {
      return null;
    }
    const channel = Math.round(Math.min(Math.max(alpha, 0), 1) * 255)
      .toString(16)
      .padStart(2, "0");
    return hex + channel;
  }

  // Choix binaire texte clair/sombre (luminance perçue YIQ) pour un texte posé sur un fond plein
  // piloté par le back ; pour garder la teinte sur un fond translucide, cf. readableTextColor()
  contrastingTextColor(value, darkColor = "#3b3f5c", lightColor = "#fff") {
    const rgb = this.hexToRgb(value);
    if (!rgb) {
      return darkColor;
    }
    const brightness = (rgb.red * 299 + rgb.green * 587 + rgb.blue * 114) / 1000;
    return brightness > 128 ? darkColor : lightColor;
  }

  hexToRgb(value) {
    const hex = this.normalizeHex(value);
    if (!hex) {
      return null;
    }
    const [red, green, blue] = [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16));
    return { red, green, blue };
  }

  relativeLuminance(value) {
    const rgb = this.hexToRgb(value);
    if (!rgb) {
      return null;
    }
    const [red, green, blue] = [rgb.red, rgb.green, rgb.blue].map((channel) => {
      const level = channel / 255;
      return level <= 0.03928 ? level / 12.92 : Math.pow((level + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
  }

  contrastRatio(first, second) {
    const [lighter, darker] = [this.relativeLuminance(first), this.relativeLuminance(second)].sort((a, b) => b - a);
    return (lighter + 0.05) / (darker + 0.05);
  }

  // Couleur de texte lisible (>= minRatio:1) sur un fond `value` translucide posé sur `backdrop` :
  // on garde la teinte et on l'assombrit jusqu'au seuil RGAA
  readableTextColor(value, backdrop = "#ffffff", alpha = 0.2, minRatio = 4.5) {
    const rgb = this.hexToRgb(value);
    const back = this.hexToRgb(backdrop);
    if (!rgb || !back) {
      return null;
    }
    const blend = (front, behind) => front * alpha + behind * (1 - alpha);
    const background = this.rgbToHex(
      blend(rgb.red, back.red),
      blend(rgb.green, back.green),
      blend(rgb.blue, back.blue),
    );
    for (let step = 0; step <= 100; step++) {
      const keep = 1 - step / 100;
      const text = this.rgbToHex(rgb.red * keep, rgb.green * keep, rgb.blue * keep);
      if (this.contrastRatio(text, background) >= minRatio) {
        return text;
      }
    }
    return "#000000";
  }

  rgbToHex(red, green, blue) {
    return (
      "#" +
      [red, green, blue]
        .map((channel) =>
          Math.round(Math.min(Math.max(channel, 0), 255))
            .toString(16)
            .padStart(2, "0"),
        )
        .join("")
    );
  }

  hexToHsv(value) {
    const rgb = this.hexToRgb(value);
    if (!rgb) {
      return null;
    }
    const [red, green, blue] = [rgb.red, rgb.green, rgb.blue].map((channel) => channel / 255);
    const max = Math.max(red, green, blue);
    const delta = max - Math.min(red, green, blue);
    let hue = 0;
    if (delta) {
      if (max === red) {
        hue = ((green - blue) / delta + 6) % 6;
      } else if (max === green) {
        hue = (blue - red) / delta + 2;
      } else {
        hue = (red - green) / delta + 4;
      }
    }
    return {
      hue: hue * 60,
      saturation: max ? (delta / max) * 100 : 0,
      value: max * 100,
    };
  }

  hsvToHex(hue, saturation, value) {
    const s = Math.min(Math.max(saturation, 0), 100) / 100;
    const v = Math.min(Math.max(value, 0), 100) / 100;
    const channel = (offset) => {
      const k = (offset + (((hue % 360) + 360) % 360) / 60) % 6;
      const level = v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
      return Math.round(level * 255)
        .toString(16)
        .padStart(2, "0");
    };
    return "#" + channel(5) + channel(3) + channel(1);
  }

  buildStyle(trainingCourse, design = null) {
    let primaryColor = "#000";
    let secondaryColor = "#000";

    if (design) {
      primaryColor = design.principalColor ? design.principalColor : "#000";
      secondaryColor = design.secondaryColor ? design.secondaryColor : "#000";
    }

    if (trainingCourse && trainingCourse.training && trainingCourse.training.institution) {
      if (trainingCourse.training.institution.principalColor) {
        primaryColor = trainingCourse.training.institution.principalColor;
      }
      if (trainingCourse.training.institution.secondaryColor) {
        secondaryColor = trainingCourse.training.institution.secondaryColor;
      }
    }

    const css =
      "" +
      "body {" +
      "--primary-color: " +
      primaryColor +
      " !important;" +
      "--secondary-color: " +
      secondaryColor +
      " !important;" +
      "}";

    let style = document.createElement("style");
    style.type = "text/css";
    style.id = "platformStyle";
    style.innerHTML = css;
    document.getElementsByTagName("head")[0].appendChild(style);
  }

  setFavicon(design) {
    const favicon = document.querySelector("link[rel~='icon']");
    if (favicon) {
      favicon.href = design && design.favicon ? pictureManager.setPicture(design.favicon, "favicon") : "/favicon.png";
    }
  }

  setPlatformStyle(trainingCourse = null, design) {
    let style = document.getElementById("platformStyle");
    if (style) {
      style.remove();
    }
    this.buildStyle(trainingCourse, design);
    this.setFavicon(design);
  }
}

export const colorManager = new ColorManager();
